import { useQuery } from "@tanstack/react-query";
import type { PortalDashboard } from "@wms/domain";
import { Alert, Button, Card, Col, Row, Space, Statistic, Typography } from "antd";
import { Link } from "react-router-dom";
import { portalApi } from "../api/portal";
import { errorMessage } from "../api/client";
import { usePortalStore } from "./store";
import { formatQty } from "../api/format";
import { PortalDesktop } from "../workspace/PortalDesktop";

/**
 * ТЗ «Рабочие столы» 31.10.2026: with «use_personal_workspace» the cabinet opens on the desktop of widgets
 * (PortalDesktop); with the option off — exactly the page of HEAD (LegacyPortalHome).
 */
export function PortalHomePage() {
  const personal = usePortalStore((s) => Boolean(s.me?.feature_options?.["use_personal_workspace"]));
  return personal ? <PortalDesktop legacy={<LegacyPortalHome />} /> : <LegacyPortalHome />;
}

/** C01: own receipts, orders, requests to clarify, available stock per unit, new invoices and documents to sign. */
export function LegacyPortalHome() {
  const { me, can } = usePortalStore();
  const dashboard = useQuery({ queryKey: ["portal", "dashboard"], queryFn: () => portalApi.get<PortalDashboard>("/dashboard"), refetchInterval: 60000 });
  const d = dashboard.data;
  return (
    <div>
      <Typography.Title level={3} style={{ marginTop: 0 }}>
        {me?.partner.name}
      </Typography.Title>
      <Space wrap style={{ marginBottom: 16 }}>
        {can("portal.requests.create") && (
          <Link to="/portal/requests?new=inbound">
            <Button type="primary" data-testid="portal-new-inbound">Сообщить о поступлении</Button>
          </Link>
        )}
        {can("portal.requests.create") && (
          <Link to="/portal/requests?new=outbound">
            <Button type="primary" data-testid="portal-new-outbound">Заказать отгрузку</Button>
          </Link>
        )}
        {can("portal.stock.read") && (
          <Link to="/portal/stock">
            <Button>Посмотреть остатки</Button>
          </Link>
        )}
        {can("portal.finance.read") && (
          <Link to="/portal/finance?tab=acts">
            <Button>Документы к подписанию{d?.to_sign ? ` (${d.to_sign})` : ""}</Button>
          </Link>
        )}
      </Space>
      {dashboard.isError && <Alert type="error" showIcon message={errorMessage(dashboard.error)} style={{ marginBottom: 16 }} />}
      <Row gutter={[12, 12]}>
        <Col xs={24} md={8}>
          <Card title="Поступления" size="small" loading={dashboard.isLoading}>
            <Statistic title="Ожидаем на складе" value={d?.receipts.expected ?? 0} />
            <Statistic title="Принимаются" value={d?.receipts.in_progress ?? 0} />
            <Statistic title="Приняты" value={d?.receipts.accepted ?? 0} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card title="Заказы на отгрузку" size="small" loading={dashboard.isLoading}>
            <Statistic title="Новые и подтверждённые" value={d?.orders.new ?? 0} />
            <Statistic title="На сборке" value={d?.orders.picking ?? 0} />
            <Statistic title="Готовы к отгрузке" value={d?.orders.ready ?? 0} />
            <Statistic title="Отгружены" value={d?.orders.shipped ?? 0} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card title="Заявки и документы" size="small" loading={dashboard.isLoading}>
            <Statistic title="Нужно уточнить" value={d?.requests.needs_clarification ?? 0} valueStyle={d?.requests.needs_clarification ? { color: "var(--wms-op-writeoff-fg)" } : undefined} />
            <Statistic title="Новые счета" value={d?.invoices.new ?? 0} />
            <Statistic title="К подписанию" value={d?.to_sign ?? 0} />
          </Card>
        </Col>
        <Col xs={24}>
          {/* CW31-B15: «Подробнее» leads only a role that may open the list; the aggregates stay as they were */}
          <Card
            title="Доступный остаток"
            size="small"
            loading={dashboard.isLoading}
            extra={can("portal.stock.read") ? <Link to="/portal/stock" data-testid="portal-stock-more">Подробнее</Link> : null}
          >
            {/* ANA-01: one number per unit, never «всего товара» across pieces and kilograms */}
            {d && d.stock.length === 0 && <Typography.Text type="secondary">На складе пока нет вашего товара с подтверждённой принадлежностью.</Typography.Text>}
            <Space size="large" wrap>
              {d?.stock.map((row) => <Statistic key={row.unit} title={`${row.unit}, доступно`} value={formatQty(row.available)} />)}
              {d && <Statistic title="Товаров" value={d.stock_products} />}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
